import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";
import { type BackfillStatus, summarize } from "./pii-backfill-summary";

export const REPAIR_STATUSES = {
  blocked: "Backfill à terminer",
  pending: "À reprendre",
  running: "En cours",
  paused: "En pause",
  done: "Repris",
} as const;

export type RepairStatus = keyof typeof REPAIR_STATUSES;

export type ShopRepairSummary = {
  status: RepairStatus;
  /** Share of documents walked by the repair run, 0 to 100. */
  percent: number;
  /**
   * Documents counted but never walked by the last finished run: a sign that
   * the run left some out. Zero says nothing, small gaps hide in the recount.
   */
  missed: number;
};

/** Where a repair run stands, read from the backfill status of its rows. */
const RUN_STATUSES: Record<BackfillStatus, RepairStatus> = {
  never: "pending",
  running: "running",
  paused: "paused",
  partial: "paused",
  done: "done",
};

const missedBy = (states: PiiBackfillStateDto[]): number =>
  states
    .filter((state) => state.isDone)
    .reduce((sum, state) => sum + Math.max(0, state.total - state.scanned), 0);

/**
 * A shop is repaired once a repair run finished on it. Before that, it waits
 * for the repair when its backfill is finished, and for the backfill
 * otherwise: an unfinished run still walks its documents.
 */
export const summarizeRepair = (
  states: PiiBackfillStateDto[],
): ShopRepairSummary => {
  const summary = summarize(states);
  const missed = missedBy(states);
  const isRepairing = states.some((state) => state.mode === "repair");
  if (isRepairing) {
    return {
      status: RUN_STATUSES[summary.status],
      percent: summary.percent,
      missed,
    };
  }
  return {
    status: summary.status === "done" ? "pending" : "blocked",
    percent: 0,
    missed,
  };
};
